import { useQuery } from '@tanstack/react-query'
import { Button, SpinnerWithBackground, Text } from '@viapeople/components'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { trpc } from '../../../utils/trpc'
import type { CrmProvider, RoleMapping } from '../types'

interface RoleMappingStepProps {
	roleMappings: RoleMapping[]
	onChange: (mappings: RoleMapping[]) => void
	/** Provider drives the CRM-side input shape: dropdown of fields for DealCloud, text for everything else. */
	provider?: CrmProvider
	/** Required when provider is dealcloud — used to discover the connection's User-type fields. */
	connectionId?: string
}

// Internal row type with a stable key for React rendering
interface RoleMappingRow extends RoleMapping {
	_id: string
}

function stripId(rows: RoleMappingRow[]): RoleMapping[] {
	return rows.filter((r) => r.crmRoleValue.trim() && r.roleId && r.formId).map(({ _id: _discarded, ...m }) => m)
}

export function RoleMappingStep({ roleMappings, onChange, provider, connectionId }: RoleMappingStepProps) {
	const { organizationId } = useParams<{ organizationId: string }>()
	const isDealCloud = provider === 'dealcloud'

	// Stable rows with internal IDs — initialised once from props on mount.
	// The component is conditionally rendered per step so props are stable at mount time.
	const [rows, setRows] = useState<RoleMappingRow[]>(() =>
		roleMappings.map((m) => ({ ...m, _id: crypto.randomUUID() })),
	)

	const { data: rolesData, isLoading: isLoadingRoles } = useQuery({
		...trpc.admin.roles.search.queryOptions({ organizationId: organizationId as string, perPage: 99999 }),
		enabled: !!organizationId,
	})

	const { data: formsData, isLoading: isLoadingForms } = useQuery({
		...trpc.admin.forms.search.queryOptions({ organizationId: organizationId as string, perPage: 99999 }),
		enabled: !!organizationId,
	})

	// DealCloud-specific: list User-type fields on the chosen entry type so admins
	// pick from real field names instead of typing strings that have to match exactly.
	const { data: dealCloudUserFields } = useQuery({
		...trpc.admin.integrations.crmConnection.listDealCloudUserFields.queryOptions({
			connectionId: connectionId ?? '',
		}),
		enabled: isDealCloud && !!connectionId,
	})

	const isLoading = isLoadingRoles || isLoadingForms
	const roles = rolesData?.data ?? []
	const forms = formsData?.data ?? []

	const handleAdd = () => {
		const newRows = [...rows, { crmRoleValue: '', roleId: '', formId: '', _id: crypto.randomUUID() }]
		setRows(newRows)
		onChange(stripId(newRows))
	}

	const handleRemove = (id: string) => {
		const newRows = rows.filter((r) => r._id !== id)
		setRows(newRows)
		onChange(stripId(newRows))
	}

	const handleChange = (id: string, field: keyof RoleMapping, value: string) => {
		const newRows = rows.map((r) => (r._id === id ? { ...r, [field]: value } : r))
		setRows(newRows)
		onChange(stripId(newRows))
	}

	if (isLoading) {
		return <SpinnerWithBackground backgroundClass='opacity-100' />
	}

	const crmValueCounts = rows.reduce<Record<string, number>>((acc, r) => {
		const key = r.crmRoleValue.trim().toLowerCase()
		if (key) acc[key] = (acc[key] ?? 0) + 1
		return acc
	}, {})

	const isIncomplete = (r: RoleMappingRow) => !r.crmRoleValue.trim() || !r.roleId || !r.formId
	const isDuplicate = (r: RoleMappingRow) =>
		r.crmRoleValue.trim() && (crmValueCounts[r.crmRoleValue.trim().toLowerCase()] ?? 0) > 1

	return (
		<div className='flex flex-col gap-6'>
			<div>
				<Text variant='medium' weight='semibold' className='mb-1'>
					Role Mapping
				</Text>
				<Text variant='small' className='text-neutral-600'>
					Map CRM role values to viaPeople roles and forms. When a deal team member's CRM role matches, they will
					receive the specified role and form for the generated cycle.
				</Text>
			</div>

			<div className='p-3 bg-primary-50 border border-primary-200 rounded-lg'>
				{isDealCloud ? (
					<Text variant='xsmall' className='text-primary-700'>
						DealCloud team members carry the <strong>name of the field they came from</strong> as their role — pick the
						User/Reference field below (e.g. <code className='bg-primary-100 px-1 rounded text-xs'>Banker_1</code>,{' '}
						<code className='bg-primary-100 px-1 rounded text-xs'>LeadBanker</code>,{' '}
						<code className='bg-primary-100 px-1 rounded text-xs'>PitchTeam</code>) and choose a viaPeople role and form
						to assign to anyone in that field. Members in unmapped fields are rejected so incorrect assignments do not
						launch.
					</Text>
				) : (
					<Text variant='xsmall' className='text-primary-700'>
						The CRM role is read from the <code className='bg-primary-100 px-1 rounded text-xs'>role</code>,{' '}
						<code className='bg-primary-100 px-1 rounded text-xs'>Role</code>,{' '}
						<code className='bg-primary-100 px-1 rounded text-xs'>ContactRole</code>, or{' '}
						<code className='bg-primary-100 px-1 rounded text-xs'>MemberRole</code> field on each team member object in
						the incoming payload. Members with no matching mapping are rejected so incorrect assignments do not launch.
					</Text>
				)}
			</div>

			{rows.length > 0 && (
				<div className='border border-neutral-200 rounded-lg overflow-hidden'>
					<div className='grid grid-cols-12 gap-2 px-4 py-3 bg-neutral-100 border-b border-neutral-200'>
						<div className='col-span-3'>
							<Text variant='xsmall' weight='semibold' className='text-neutral-600 uppercase'>
								{isDealCloud ? 'DealCloud Field' : 'CRM Role Value'}
							</Text>
						</div>
						<div className='col-span-4'>
							<Text variant='xsmall' weight='semibold' className='text-neutral-600 uppercase'>
								viaPeople Role
							</Text>
						</div>
						<div className='col-span-4'>
							<Text variant='xsmall' weight='semibold' className='text-neutral-600 uppercase'>
								viaPeople Form
							</Text>
						</div>
						<div className='col-span-1' />
					</div>

					{rows.map((row) => (
						<div
							key={row._id}
							className={`grid grid-cols-12 gap-2 px-4 py-3 border-b last:border-b-0 items-center ${isIncomplete(row) || isDuplicate(row) ? 'border-danger-200 bg-danger-50' : 'border-neutral-100 bg-white hover:bg-neutral-50'}`}>
							<div className='col-span-3'>
								{isDealCloud ? (
									<select
										value={row.crmRoleValue}
										onChange={(e) => handleChange(row._id, 'crmRoleValue', e.target.value)}
										className='w-full px-3 py-1.5 rounded border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'>
										<option value=''>Select field…</option>
										{(dealCloudUserFields ?? []).map((f) => (
											<option key={f.id} value={f.name}>
												{f.name}
											</option>
										))}
										{/* If a previously-saved value isn't in the field list, keep it visible */}
										{row.crmRoleValue && !(dealCloudUserFields ?? []).some((f) => f.name === row.crmRoleValue) && (
											<option value={row.crmRoleValue}>{row.crmRoleValue}</option>
										)}
									</select>
								) : (
									<input
										type='text'
										value={row.crmRoleValue}
										onChange={(e) => handleChange(row._id, 'crmRoleValue', e.target.value)}
										placeholder='e.g., AE'
										className='w-full px-3 py-1.5 rounded border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
									/>
								)}
							</div>
							<div className='col-span-4'>
								<select
									value={row.roleId}
									onChange={(e) => handleChange(row._id, 'roleId', e.target.value)}
									className='w-full px-3 py-1.5 rounded border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'>
									<option value=''>Select role...</option>
									{roles.map((role: { id: string; name: string }) => (
										<option key={role.id} value={role.id}>
											{role.name}
										</option>
									))}
								</select>
							</div>
							<div className='col-span-4'>
								<select
									value={row.formId}
									onChange={(e) => handleChange(row._id, 'formId', e.target.value)}
									className='w-full px-3 py-1.5 rounded border border-neutral-300 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'>
									<option value=''>Select form...</option>
									{forms.map((form: { id: string; name: string }) => (
										<option key={form.id} value={form.id}>
											{form.name}
										</option>
									))}
								</select>
							</div>
							<div className='col-span-1 flex justify-center'>
								<button
									type='button'
									onClick={() => handleRemove(row._id)}
									className='text-neutral-400 hover:text-danger-600 transition-colors'
									title='Remove mapping'>
									<span className='sr-only'>Delete</span>
									<svg
										xmlns='http://www.w3.org/2000/svg'
										fill='none'
										viewBox='0 0 24 24'
										strokeWidth={1.5}
										stroke='currentColor'
										className='w-5 h-5'
										aria-hidden='true'>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											d='M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0'
										/>
									</svg>
								</button>
							</div>
						</div>
					))}
				</div>
			)}

			{rows.length === 0 && (
				<div className='p-6 border-2 border-dashed border-neutral-300 rounded-lg text-center'>
					<Text variant='small' className='text-neutral-500'>
						No role mappings configured. All members will receive the template's default role and form.
					</Text>
				</div>
			)}

			<div>
				<Button variant='secondary' size='small' onClick={handleAdd}>
					+ Add Role Mapping
				</Button>
			</div>
		</div>
	)
}
